/**
 * Declarative catalogs + resolved-config types for the OMS v3 Sales Order Review
 * page's configurable metrics row and line-item columns. The catalogs are the
 * "base catalog" of what's available; an org's `core_org_ui_section_configs`
 * entry (sectionId "sales-order-draft") picks/orders/overrides from them.
 *
 * Metric *values* are computed on the frontend from line data (the spec computes
 * from line data and recomputes live on edit); these types carry only the
 * declarative definition (which metrics, order, labels, tooltips, formats).
 */

export type SalesOrderMetricFormat =
  "currency" | "percent" | "integer" | "number" | "date" | "string";

export interface SalesOrderMetricDef {
  key: string;
  label: string;
  tooltip: string;
  format: SalesOrderMetricFormat;
  /** Needs current cost data; renders "—" when cost/margin is unavailable. */
  requiresCost?: boolean;
}

/** Every metric the review page knows how to compute (spec §5.3 + Americas). */
export const SALES_ORDER_METRIC_CATALOG: SalesOrderMetricDef[] = [
  {
    key: "total_revenue",
    label: "Total Revenue",
    tooltip: "Sum of (unit selling price × quantity) across all line items.",
    format: "currency",
  },
  {
    key: "total_margin",
    label: "Total Margin",
    tooltip:
      "Sum of (unit selling price − current cost) × quantity across line items that have a current cost.",
    format: "currency",
    requiresCost: true,
  },
  {
    key: "total_margin_pct",
    label: "Total Margin %",
    tooltip: "Total margin ÷ total revenue.",
    format: "percent",
    requiresCost: true,
  },
  {
    key: "total_discount_pct",
    label: "Total Discount %",
    tooltip: "Total off-list dollars ÷ list-price revenue.",
    format: "percent",
  },
  {
    key: "total_price_impact",
    label: "Total Price Impact",
    tooltip: "Net effect of year-over-year price changes across matched items.",
    format: "currency",
    requiresCost: true,
  },
  {
    key: "total_amount",
    label: "Total Amount",
    tooltip: "Sum of unit price × quantity across all line items.",
    format: "currency",
  },
  {
    key: "line_count",
    label: "Line Items",
    tooltip: "Number of line items on the order.",
    format: "integer",
  },
  {
    key: "requested_ship",
    label: "Requested Ship",
    tooltip: "Customer-requested ship date from the PO.",
    format: "date",
  },
];

/** Horizontal alignment of a line column's header + cells. */
export type SalesOrderColumnAlign = "left" | "center" | "right";

export interface SalesOrderLineColumnDef {
  key: string;
  label: string;
  defaultWidth?: string;
  alignRight?: boolean;
  /** Catalog default alignment; org config can override per column. */
  align?: SalesOrderColumnAlign;
  /** Optional help text shown as a tooltip on the column header. */
  tooltip?: string;
}

/** Every line-item column the review table can render (spec §7.1). */
export const SALES_ORDER_LINE_COLUMN_CATALOG: SalesOrderLineColumnDef[] = [
  { key: "item", label: "Item", defaultWidth: "26%" },
  { key: "qty", label: "Qty", defaultWidth: "7%", alignRight: true },
  { key: "uom", label: "UOM", defaultWidth: "7%" },
  { key: "price", label: "Unit price", defaultWidth: "18%" },
  { key: "total", label: "Total", defaultWidth: "12%", alignRight: true },
  { key: "inventory", label: "Inventory", defaultWidth: "12%" },
  { key: "warehouse", label: "WH", defaultWidth: "6%" },
  { key: "lead", label: "Lead time", defaultWidth: "9%" },
  { key: "status", label: "Status", defaultWidth: "16%" },
  {
    key: "discounted_price",
    label: "Discounted price",
    defaultWidth: "12%",
    alignRight: true,
    tooltip:
      "Unit price after the line's applied discounts (Discounts v2); the PO unit price when none are applied.",
  },
  {
    key: "match",
    label: "Match",
    defaultWidth: "5%",
    align: "center",
    tooltip:
      "Whether the line is matched to a product. Click the icon to match or change it.",
  },
];

/** Safe, cost-free defaults used when an org has no configured metrics/columns. */
export const DEFAULT_SALES_ORDER_METRICS = [
  "total_revenue",
  "total_discount_pct",
  "line_count",
];
export const DEFAULT_SALES_ORDER_LINE_COLUMNS = [
  "item",
  "qty",
  "uom",
  "price",
  "total",
  "status",
];

export type SalesOrderFieldControl =
  "text" | "select" | "date" | "hidden" | "readonly" | "textarea";

/** Field value format — mirrors `FieldEntry["format"]` (distinct from metric formats). */
export type SalesOrderFieldFormat =
  "string" | "number" | "integer" | "float" | "currency" | "date" | "boolean";

/**
 * An addable field for the Sales Order Review page. The "base catalog" of
 * header + line fields an org can pick from (spec §5.5). Identity is
 * `${fieldGroup}:${id}` (the `warehouse` id exists in both groups). Resolver /
 * dataPath are canonical defaults carried onto a new field when added; the
 * resolver picker is a follow-up.
 */
export interface SalesOrderFieldDef {
  id: string;
  label: string;
  fieldGroup: "header" | "lineItem";
  control: SalesOrderFieldControl;
  /** Dot-notation source path; absent for resolver-driven fields. */
  dataPath?: string;
  format?: SalesOrderFieldFormat;
  /** Canonical resolver carried onto the field when added. */
  resolver?: string;
  /** Optional help text shown as a tooltip next to the field label. */
  tooltip?: string;
}

/** Stable identity for a field (id collides across groups, e.g. warehouse). */
export function salesOrderFieldKey(field: {
  id: string;
  fieldGroup?: "header" | "lineItem" | null;
}): string {
  return `${field.fieldGroup ?? "header"}:${field.id}`;
}

/** Base catalog of sales-order-draft fields (union of known org configs). */
export const SALES_ORDER_FIELD_CATALOG: SalesOrderFieldDef[] = [
  // --- Header ---
  {
    id: "customer",
    label: "Customer",
    fieldGroup: "header",
    control: "select",
    dataPath: "customerId",
  },
  {
    id: "customerPurchaseOrderNumber",
    label: "Customer PO #",
    fieldGroup: "header",
    control: "text",
    dataPath: "purchaseOrderNumber",
  },
  {
    id: "orderType",
    label: "Order Type",
    fieldGroup: "header",
    control: "select",
    dataPath: "orderType",
    resolver: "planner_code_order_type",
  },
  {
    id: "priceList",
    label: "Price List",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "priceList",
    resolver: "location_price_list_name",
  },
  {
    id: "currency",
    label: "Currency",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "currency",
    resolver: "location_price_list_currency",
  },
  {
    id: "warehouse",
    label: "Warehouse",
    fieldGroup: "header",
    control: "select",
    dataPath: "warehouse",
    resolver: "planner_code_warehouse",
  },
  {
    id: "shipToLocation",
    label: "Ship To Location",
    fieldGroup: "header",
    control: "select",
    dataPath: "shipToLocationId",
  },
  {
    id: "orderedDate",
    label: "Ordered Date",
    fieldGroup: "header",
    control: "date",
    dataPath: "orderDate",
    format: "date",
  },
  {
    id: "receivedDate",
    label: "Received Date",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "receivedDate",
    format: "date",
    tooltip: "When this order was received (e.g. the inbound email timestamp).",
  },
  {
    id: "createdAt",
    label: "Created At",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "createdAt",
    format: "date",
    tooltip: "When this draft sales order record was created.",
  },
  {
    id: "updatedAt",
    label: "Updated At",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "updatedAt",
    format: "date",
    tooltip: "When this draft sales order record was last updated.",
  },
  {
    id: "requestDate",
    label: "Request Date",
    fieldGroup: "header",
    control: "date",
    dataPath: "deliveryDate",
    format: "date",
    resolver: "request_date",
  },
  {
    id: "shippingMethod",
    label: "Shipping Method",
    fieldGroup: "header",
    control: "select",
    dataPath: "shippingMethod",
    resolver: "shipping_method_from_core_enum",
  },
  {
    id: "erpShippingMethod",
    label: "ERP Shipping Method",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "erpShippingMethod",
    resolver: "shipping_map_field",
  },
  {
    id: "modeOfTransport",
    label: "Mode of Transport",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "modeOfTransport",
    resolver: "shipping_map_field",
  },
  {
    id: "paymentTerms",
    label: "Payment Terms",
    fieldGroup: "header",
    control: "select",
    dataPath: "paymentTerms",
    resolver: "payment_terms_from_core_enum",
    tooltip:
      "Payment terms for the order. With a synced PaymentTerms enum the dropdown lists the ERP's terms (the pick is what the ERP submission sends) and defaults to the one matching the PO's stated terms; otherwise the PO's terms as free text. Terms longer than the organization's standard are flagged by the extraction check.",
  },
  {
    id: "shippingTerms",
    label: "Shipping Terms (PO)",
    fieldGroup: "header",
    control: "text",
    resolver: "extracted_data_field",
    tooltip:
      "Shipping terms (Incoterm) as stated on the customer's purchase order. Terms that differ from the organization's standard are flagged by the extraction check.",
  },
  {
    id: "freightTerms",
    label: "Freight Terms",
    fieldGroup: "header",
    control: "select",
    dataPath: "freightTerms",
    resolver: "freight_terms_from_core_enum",
  },
  {
    id: "freightCharges",
    label: "Freight Charges",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "freightCharges",
    resolver: "shipping_map_field",
  },
  {
    id: "freightCarrierInstructions",
    label: "Freight Carrier Instructions",
    fieldGroup: "header",
    control: "textarea",
    resolver: "extracted_data_field",
  },
  {
    id: "salesCredit",
    label: "Sales Credit",
    fieldGroup: "header",
    control: "select",
    dataPath: "salesCredit",
    resolver: "sales_credit_from_customer_salesperson",
    tooltip:
      "Salesperson credited with the order's revenue. Defaults to the customer's primary salesperson; options come from the org's Salespersons enum.",
  },
  {
    id: "supplier",
    label: "Supplier",
    fieldGroup: "header",
    control: "select",
    dataPath: "supplier",
    resolver: "apac_supplier_from_variant_source",
  },
  {
    id: "supplierSite",
    label: "Supplier Site",
    fieldGroup: "header",
    control: "select",
    dataPath: "supplierSite",
    resolver: "apac_supplier_site",
  },
  {
    id: "total",
    label: "Total",
    fieldGroup: "header",
    control: "readonly",
    dataPath: "totalAmount",
    format: "currency",
  },
  // --- Line items ---
  {
    id: "lineNumber",
    label: "#",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "lineNumber",
  },
  {
    id: "customerIdentifier",
    label: "Item",
    fieldGroup: "lineItem",
    control: "text",
    dataPath: "customerIdentifier",
  },
  {
    id: "customerDescription",
    label: "Description",
    fieldGroup: "lineItem",
    control: "text",
    dataPath: "customerDescription",
  },
  {
    id: "quantity",
    label: "Quantity",
    fieldGroup: "lineItem",
    control: "text",
    dataPath: "quantity",
  },
  {
    id: "unit",
    label: "UOM",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "unit",
  },
  {
    id: "unitPrice",
    label: "Unit Selling Price",
    fieldGroup: "lineItem",
    control: "text",
    dataPath: "unitPrice",
    format: "currency",
  },
  {
    id: "listPrice",
    label: "List Price",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "listPrice",
    format: "currency",
  },
  {
    id: "totalPrice",
    label: "Total Price",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "totalPrice",
    format: "currency",
  },
  {
    id: "discountPercent",
    label: "Discount %",
    fieldGroup: "lineItem",
    control: "readonly",
    // Stamped at ingest by effective-price derivation when the PO states a
    // per-line discount (see deriveEffectiveLinePricing).
    dataPath: "additionalData.poDiscountPercent",
    format: "number",
  },
  {
    id: "priceStatus",
    label: "Price Status",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "priceStatus",
  },
  {
    id: "inventoryStatus",
    label: "Inventory Status",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "inventoryStatus",
  },
  {
    id: "leadTimeDays",
    label: "Lead Time",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "leadTimeDays",
    format: "integer",
  },
  {
    id: "warehouse",
    label: "Warehouse",
    fieldGroup: "lineItem",
    control: "select",
    dataPath: "warehouse",
  },
  {
    id: "salesProductType",
    label: "Sales Product Type",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "salesProductType",
  },
  {
    id: "rescheduledDate",
    label: "Rescheduled Date",
    fieldGroup: "lineItem",
    control: "date",
    dataPath: "rescheduledDate",
    format: "date",
  },
  {
    id: "status",
    label: "Status",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "status",
  },
  {
    id: "actions",
    label: "Actions",
    fieldGroup: "lineItem",
    control: "readonly",
    dataPath: "actions",
  },
];

/** Sensible default field set (by `${fieldGroup}:${id}`) for a fresh config. */
export const DEFAULT_SALES_ORDER_FIELDS = [
  "header:customer",
  "header:customerPurchaseOrderNumber",
  "header:orderType",
  "header:priceList",
  "header:shipToLocation",
  "header:receivedDate",
  "header:requestDate",
  "header:total",
  "lineItem:customerIdentifier",
  "lineItem:customerDescription",
  "lineItem:quantity",
  "lineItem:unit",
  "lineItem:unitPrice",
  "lineItem:listPrice",
  "lineItem:status",
];

/** Aggregation a custom metric applies across the order's lines. */
export type SalesOrderMetricAggregation = "count" | "sum" | "average";

/** A metric resolved against the catalog + org overrides (no computed value). */
export interface ResolvedSalesOrderMetric {
  key: string;
  label: string;
  tooltip: string;
  format: SalesOrderMetricFormat;
  requiresCost: boolean;
  /**
   * Custom (org-added) metric: how the per-line source value is aggregated
   * across the order's lines (count of populated lines / sum / average). Absent
   * for catalog metrics.
   */
  aggregation?: SalesOrderMetricAggregation;
  /** Custom metric source: a single line field aggregated across the lines. */
  dataPath?: string;
  /**
   * Custom metric source (alternative to `dataPath`): a two-field per-line
   * computation aggregated across the lines (e.g. avg of listPrice − unitPrice).
   */
  compute?: SalesOrderColumnCompute;
  /** Decimal places for a custom metric's numeric/percent/currency value. */
  decimals?: number;
}

/**
 * Arithmetic operators a computed custom column can apply between its two
 * operand fields. `percent_off` = `(right − left) / right × 100` (e.g. the
 * discount a selling price represents against list).
 */
export type SalesOrderColumnOperator =
  "subtract" | "add" | "multiply" | "divide" | "percent_off";

/**
 * A computed custom column: resolve two numeric dot-paths off the line item and
 * combine them with `op`. Mutually exclusive with a column's single `dataPath`.
 */
export interface SalesOrderColumnCompute {
  /** Left operand dot-path into the line item. */
  left: string;
  op: SalesOrderColumnOperator;
  /** Right operand dot-path into the line item. */
  right: string;
}

/**
 * Display format for a custom line column. A superset of the header field
 * formats with `"percent"` (e.g. a computed discount-percent column) — kept
 * column-local so the shared `SalesOrderFieldFormat` (mirroring `FieldEntry`)
 * stays untouched.
 */
export type SalesOrderColumnFormat = SalesOrderFieldFormat | "percent";

/**
 * A subtle second line rendered under a column's main value, with an optional
 * short prefix — the v2 list-vs-PO comparison look (e.g. main "PO: $33,142.00"
 * over "VJ: $34,506.61"). Sources a single line field or a two-field compute,
 * exactly like a custom column's main value.
 */
export interface SalesOrderColumnSubline {
  /** Short label rendered before the value (e.g. "VJ"). */
  prefix?: string;
  /** Dot-path into the line item (mutually exclusive with `compute`). */
  dataPath?: string;
  compute?: SalesOrderColumnCompute;
  format?: SalesOrderColumnFormat;
  decimals?: number;
}

/** A line column resolved against the catalog + org width override. */
export interface ResolvedSalesOrderColumn {
  key: string;
  label: string;
  width: string | null;
  /** Kept for older consumers; always `align === "right"`. */
  alignRight: boolean;
  /** Effective alignment: org override, else catalog default, else left. */
  align: SalesOrderColumnAlign;
  /**
   * Dot-notation path into the line item (e.g. "additionalData.sku") for custom
   * columns the org added by picking a field from real orders. Absent for the
   * built-in catalog columns, which the line table renders with bespoke cells.
   */
  dataPath?: string;
  /** Display format for a custom (dataPath-driven or computed) column's value. */
  format?: SalesOrderColumnFormat;
  /** Two-field arithmetic for a computed custom column (instead of `dataPath`). */
  compute?: SalesOrderColumnCompute;
  /** Decimal places for numeric/percent formatting (custom columns). */
  decimals?: number;
  /** Optional help text shown as a tooltip on the column header. */
  tooltip?: string;
  /** Optional second line under the main value (works on any column). */
  subline?: SalesOrderColumnSubline;
  /**
   * Whether the line table renders this column's cells as inline-editable
   * (manual override). Only honored for data paths in
   * `EDITABLE_LINE_DATA_PATHS` — config alone can never make a non-writable
   * path editable.
   */
  editable?: boolean;
}

/** How an editable column cell writes back: the patch field + widget type. */
export interface EditableLineDataPathSpec {
  /** Field on the draft-line update patch the edit commits to. */
  patchKey:
    | "quantity"
    | "unitPrice"
    | "amount"
    | "unit"
    | "warehouse"
    | "customerIdentifier"
    | "customerDescription";
  widget: "number" | "text";
}

/**
 * The line-item data paths a column may be marked editable on — the paths
 * that map onto genuinely writable draft-line fields. Everything else
 * (stamped facts like listPrice, discount-stack outputs, server-managed
 * inventory/lead-time) is derived and would be silently overwritten, so the
 * editor offers the toggle only for these.
 *
 * `discount.effectiveUnitPrice` is the render-time merge most orgs bind
 * their Unit Price column to; a manual override there writes the line's
 * `unitPrice` (the PO price the effective price falls back to).
 */
export const EDITABLE_LINE_DATA_PATHS: Record<
  string,
  EditableLineDataPathSpec
> = {
  quantity: { patchKey: "quantity", widget: "number" },
  unitPrice: { patchKey: "unitPrice", widget: "number" },
  "discount.effectiveUnitPrice": { patchKey: "unitPrice", widget: "number" },
  amount: { patchKey: "amount", widget: "number" },
  unit: { patchKey: "unit", widget: "text" },
  warehouse: { patchKey: "warehouse", widget: "text" },
  customerIdentifier: { patchKey: "customerIdentifier", widget: "text" },
  customerDescription: { patchKey: "customerDescription", widget: "text" },
};

/** A header (order-details) field resolved for the review page's top section. */
export interface ResolvedSalesOrderHeaderField {
  id: string;
  label: string;
  dataPath?: string;
  /** Presentation hints (string-typed; the page renders by these). */
  control?: string;
  format?: string;
  /** Whether the page renders an interactive edit widget instead of plain text. */
  editable?: boolean;
  /** Horizontal span on the review page's 12-column grid (1=¼, 2=½, 3=¾, 4=full, 5=⅓). */
  colSpan?: number;
  /** Selectable options for `control: "select"` fields (e.g. shipping method /
   *  freight terms from the org's core enums). `value` is the stored value. */
  options?: { value: string; label: string }[];
  /** Optional help text shown as a tooltip next to the field label. */
  tooltip?: string;
}

/** The review page's resolved UI config, served alongside the draft. */
export interface SalesOrderUiConfig {
  metrics: ResolvedSalesOrderMetric[];
  lineColumns: ResolvedSalesOrderColumn[];
  /** Header (top-section) fields — the "Order details" tab, config-driven. */
  headerFields: ResolvedSalesOrderHeaderField[];
}
